/**
 * Service layer — PDF rendering via api/render.js (same AlderCortex project).
 *
 * api/render.js uses Playwright + @sparticuz/chromium (1 GB / 60s function)
 * to render HTML templates to PDF, upload to Supabase Storage, and write the
 * public URL back to the source record. No separate Vercel project needed.
 */

async function callPdfService(payload) {
  const token = await window.__clerkGetToken()
  const res = await fetch('/api/render', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    const b = await res.json().catch(() => ({}))
    throw new Error(b.error || 'PDF service error: ' + res.status)
  }
  return res.json()
}

/**
 * Generate a Submittal Review Transmittal PDF.
 *
 * @param {object} opts
 * @param {string} opts.submittalId          Supabase submittal id
 * @param {string} opts.revisionId           Supabase submittal_revisions id
 * @param {number} opts.revisionNumber       Revision number (0, 1, 2, …)
 * @param {string} opts.projectName          Project name for the header
 * @param {string} [opts.projectNumber]      Project number
 * @param {string} [opts.submittalNumber]    Firm-assigned submittal # (e.g. "06 10 00-001")
 * @param {string} [opts.gcNumber]           GC submittal #
 * @param {string} [opts.type]               Shop Drawings / Product Data / Sample / Other
 * @param {string} [opts.specSectionNumber]  CSI section number
 * @param {string} [opts.specSectionName]    CSI section name
 * @param {string|string[]} [opts.reviewer]  Reviewing party name(s)
 * @param {string[]} [opts.disposition]      Disposition selection(s)
 * @param {string} [opts.dateReceived]       ISO date (YYYY-MM-DD)
 * @param {string} [opts.dateSent]
 * @param {string} [opts.dateReceivedBack]
 * @param {string} [opts.dateReturned]
 * @param {string} [opts.reviewComments]     Markdown; rendered as plain text in the PDF
 * @param {string} [opts.signerName]         Full name of the reviewer (logged-in user)
 * @param {string} [opts.signatureUrl]       URL of the reviewer's signature image (Airtable attachment)
 *
 * @returns {Promise<{ url: string }>}  Public URL of the generated PDF.
 */
export async function generateSubmittalTransmittal(opts) {
  const ts = Date.now()
  const storagePath = `submittals/${opts.submittalId}/rev-${opts.revisionNumber}_${ts}.pdf`

  return callPdfService({
    template: 'submittal-transmittal',
    data: {
      projectName:       opts.projectName || '',
      projectNumber:     opts.projectNumber || '',
      submittalNumber:   opts.submittalNumber || '',
      gcNumber:          opts.gcNumber || '',
      type:              opts.type || '',
      revisionNumber:    opts.revisionNumber,
      specSectionNumber: opts.specSectionNumber || '',
      specSectionName:   opts.specSectionName || '',
      reviewer:          opts.reviewer || '',
      disposition:       opts.disposition || [],
      dateReceived:      opts.dateReceived || null,
      dateSent:          opts.dateSent || null,
      dateReceivedBack:  opts.dateReceivedBack || null,
      dateReturned:      opts.dateReturned || null,
      reviewComments:    opts.reviewComments || '',
      signerName:        opts.signerName || '',
      signatureUrl:      opts.signatureUrl || null,
    },
    storageBucket: 'submittal-pdfs',
    storagePath,
    writeBack: {
      table:   'submittal_revisions',
      id:      opts.revisionId,
      urlCol:  'transmittal_pdf_url',
      pathCol: 'transmittal_pdf_path',
    },
  })
}
